import {
  Navigator,
  flightTime,
  type ClusterId,
  type NavTarget,
  type SystemId,
  getGalaxyMap,
  getSystemSummary,
  type RoutePlan,
  type Vec3,
} from '@interspacial/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import {
  buildClusterLayer,
  buildGalaxyLayer,
  buildSystemLayer,
  type Layer,
} from './starmapScene';
import { LodTracker, SYSTEM_RENDER_SCALE } from './lod';
import { pinTarget } from './SearchPanel';
import { GAME_SCALE } from './systemScene';
import type { FlightState } from '../flightState';
import { frameDistance } from './framing';
import { createSpaceBackground } from './spaceBackground';
import { mountViewport } from './viewport';
import { SearchPanel } from './SearchPanel';
import { currentRoute, subscribeRoute } from './routeChannel';

const PANEL: React.CSSProperties = {
  position: 'absolute',
  background: 'rgba(8, 14, 24, 0.82)',
  border: '1px solid rgba(127, 212, 255, 0.22)',
  padding: '10px 14px',
  fontSize: 13,
  lineHeight: 1.5,
};

const FOV = 50;

/** Fired by the UI to ask the live scene to fly somewhere. */
const JUMP_EVENT = 'interspacial:jump';

/**
 * Where a jump leaves you.
 *
 * `system` puts you close enough to read the orbits. `cluster` stops further
 * out, with the system as the pivot but its neighbours still on screen — which
 * is the useful place to arrive when the question is "where am I", not "what is
 * in this system".
 */
interface Jump {
  systemId: string;
  level: 'cluster' | 'system';
}

/** Remembers where the map was left, so reopening it does not reset the view. */
const VIEW_STORAGE_KEY = 'interspacial:starmap-view';

interface StoredView {
  seed: string;
  target: [number, number, number];
  position: [number, number, number];
  /**
   * Which system the camera had resolved when it was saved.
   *
   * The coordinates alone say where the view was and not what it was *of*, and
   * "what was it of" is the only question worth asking when deciding whether to
   * restore it. Optional because a view written before this existed is still a
   * perfectly good view — it simply cannot vouch for itself, and is treated as
   * not on the route.
   */
  systemId?: string;
}

function readStoredView(seed: string): StoredView | null {
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredView;
    // A view saved against another galaxy means nothing in this one.
    if (parsed.seed !== seed) return null;
    if (parsed.target?.length !== 3 || parsed.position?.length !== 3) return null;
    if ([...parsed.target, ...parsed.position].some((n) => !Number.isFinite(n))) return null;
    return parsed;
  } catch {
    // Storage can be unavailable or hold something we did not write. Falling
    // back to the default framing is always safe, so this is not worth raising.
    return null;
  }
}

interface Readout {
  tier: string;
  title: string;
  lines: string[];
}

export interface StarmapProps {
  seed: string;
  /**
   * Change the galaxy.
   *
   * Kept on the interface but no longer offered here: a seed box is a generator
   * control, and putting one on the chart invited a player to replace the world
   * they were navigating. The admin console is where that belongs.
   */
  onSeedChange?: (seed: string) => void;
  /** Leave the map and drop into a system at game detail. */
  onEnterSystem: (systemId: string) => void;
  /** Where the ship is. The map opens here the first time, and can return to it. */
  currentSystemId?: string;
  /**
   * The ship itself, if it is out flying.
   *
   * A chart of the system you are in that does not say where in it you are is a
   * chart of somewhere else. This is the marker that used to live in the old
   * system view; the map's system layer draws the same bodies, so it is the
   * natural home for it now that view is gone.
   */
  ship?: FlightState | null;
  /**
   * The station we are sitting on, by name, when we are not out flying.
   *
   * A chart that only marks you while you are in space is a chart that goes
   * blank the moment you land — which is the one time you are certain to be
   * looking at it, working out where to go next.
   */
  dockedAt?: string | null;
  /**
   * What the ship in the berth can actually cross in one hop.
   *
   * Passed down to the route planner, which used to ask the player to choose an
   * engine tier from a dropdown — a route planned for a drive you do not own is
   * a plan that lies.
   */
  jumpRange?: number;
  /** And what it can cross between clusters, which is a separate reach. */
  clusterRange?: number;
  /**
   * Drawn as scenery rather than as a tool.
   *
   * The menu puts the galaxy behind a title card, and every panel on top of it
   * is then a control for something you are not doing yet — a seed box on a
   * splash screen invites you to break the world before you have seen it. So
   * the chrome goes and the camera stops taking input.
   */
  asBackdrop?: boolean;
  /**
   * Leave the chart without going anywhere.
   *
   * Only supplied when there is somewhere to go back *to* — opened from the
   * cockpit the map is a thing you consult mid-flight, and entering a system
   * from it would be a teleport that made the jump module pointless. Opened
   * from a berth there is no ship in space to return to, so entering a system
   * remains the way out.
   */
  onClose?: () => void;
  /**
   * The ship, live, rather than as it was when the chart opened.
   *
   * A ref because the chart is now an overlay over a cockpit that is still
   * flying, so the marker has to follow a ship that is *moving* — and a prop
   * read once when the scene was built cannot. The scene effect is deliberately
   * not rebuilt on prop changes; this is how the loop reaches the current value
   * without one.
   */
  shipRef?: { current: FlightState | null };
}

/**
 * Point the chart at a system, from anywhere.
 *
 * A window event rather than a prop or a handle, because the chart's camera
 * lives inside a `useEffect` that is deliberately not rebuilt when props
 * change — rebuilding it would tear down the galaxy to answer a button. The
 * event is consumed on the next frame by the loop that owns the camera.
 *
 * Exported so the app's own chrome can drive it: the row of buttons in the
 * corner belongs to the app, not to this view, and it had grown a second copy
 * of every control this one offered.
 */
export function focusSystem(systemId: string, level: Jump['level'] = 'cluster'): void {
  window.dispatchEvent(new CustomEvent<Jump>(JUMP_EVENT, { detail: { systemId, level } }));
}

export function Starmap({
  seed,
  onEnterSystem,
  dockedAt = null,
  jumpRange,
  clusterRange,
  onClose,
  shipRef,
  currentSystemId,
  ship = null,
  asBackdrop = false,
}: StarmapProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const labelLayerRef = useRef<HTMLDivElement>(null);



  const [readout, setReadout] = useState<Readout>({ tier: 'galaxy', title: 'Galaxy', lines: [] });

  // Whatever system the camera has resolved, so the search panel can default
  // its origin to where you actually are.
  const [focusedSystemId, setFocusedSystemId] = useState<string | null>(null);

  /**
   * Whatever the pivot is currently sitting on, ready to be pinned.
   *
   * The chart can point at things the search box cannot name — you steer onto a
   * moon and it is simply *there* — so "pin what I am looking at" has to be its
   * own action rather than a second way of typing a name.
   */
  const [underCursor, setUnderCursor] = useState<NavTarget | null>(null);

  // One index per seed, shared by the panel and reused across renders — it
  // walks the whole galaxy to build, so rebuilding per keystroke would stall.
  const navigator = useMemo(() => new Navigator(seed), [seed]);

  const jumpTo = focusSystem;

  /** So the scene's key handler can reach the latest callback. */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const mount = mountRef.current;
    const labelLayer = labelLayerRef.current;
    if (!mount || !labelLayer) return;

    const map = getGalaxyMap(seed);
    const totals = map.reduce(
      (acc, entry) => {
        acc.systems += entry.systems.length;
        acc.dockable += entry.systems.filter((s) => s.stationCount > 0).length;
        return acc;
      },
      { systems: 0, dockable: 0 },
    );
    const galaxyLines = [
      `${map.length} clusters`,
      `${totals.systems.toLocaleString()} systems`,
      `${totals.dockable.toLocaleString()} dockable (${Math.round((totals.dockable / totals.systems) * 100)}%)`,
    ];

    const extent = map.reduce(
      (max, { cluster }) =>
        Math.max(max, Math.hypot(cluster.position.x, cluster.position.y) + cluster.radius),
      0,
    );

    const scene = new THREE.Scene();
    const background = createSpaceBackground();
    scene.add(background.mesh);
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.1);
    keyLight.position.set(1, -1, 1).normalize();
    scene.add(keyLight);

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    // The camera has to work from a 300,000-unit galaxy down to a 5-unit
    // planet, so a logarithmic depth buffer is not optional here — a linear one
    // z-fights badly at that range.
    const camera = new THREE.PerspectiveCamera(FOV, width / height, 0.05, 2_000_000);
    camera.up.set(0, 0, 1);

    const view = mountViewport(
      mount,
      (w, h) => {
        width = w;
        height = h;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      },
      { logarithmicDepthBuffer: true },
    );
    const renderer = view.renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    // Locked as a backdrop. Damping stays on so the view still settles rather
    // than stopping dead on the frame the menu opens.
    controls.enabled = !asBackdrop;
    controls.dampingFactor = 0.08;
    // Zoom has to span five orders of magnitude for the tiers to be reachable.
    controls.minDistance = 4;
    controls.maxDistance = extent * 4;
    // Left stays unbound: it is free for selecting.
    controls.mouseButtons = { LEFT: null, MIDDLE: THREE.MOUSE.ROTATE, RIGHT: THREE.MOUSE.PAN };
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.screenSpacePanning = true;
    // Zooming should slow down as you close in, or the last decade of scale
    // flies past in one wheel notch.
    controls.zoomSpeed = 1.1;

    /*
     * ── Where the map opens ──────────────────────────────────────────────────
     *
     * A remembered view used to win outright, on the grounds that the chart is a
     * tool you come back to and resetting it throws away the navigating you just
     * did. That is true of the view you left *while planning* and false of every
     * other one — and the other ones are the common case, because the chart is
     * mostly used to look somewhere, decide against it, and fly on.
     *
     * The result was a map that opened three clusters from the ship, showing a
     * system the player had already dismissed, with no clue where they were.
     *
     * So the memory now has to earn itself: a stored view is restored only if it
     * was looking at a system **on the current route**, which is the one case
     * where it is still the thing you were working on. Anything else opens on
     * the ship, because "where am I" is the question a chart is opened with.
     */
    const stored = readStoredView(seed);
    const route = currentRoute();

    const remembered =
      stored && stored.systemId && route?.path.includes(stored.systemId as SystemId)
        ? stored
        : null;

    // Framing the ship's system goes through the jump path rather than setting
    // the camera here, so there is one implementation of "arrive at a system at
    // this tier". It is applied on the first frame, once that path exists.
    const openOn: Jump | null =
      !remembered && currentSystemId ? { systemId: currentSystemId, level: 'cluster' } : null;

    if (remembered) {
      const stored = remembered;
      controls.target.set(...stored.target);
      camera.position.set(...stored.position);
    } else if (!openOn) {
      const startDistance = extent / Math.tan((FOV * Math.PI) / 360);
      camera.position.set(0, -startDistance * 0.7, startDistance * 0.62);
      controls.target.set(0, 0, 0);
    }

    const blockMenu = (e: Event) => e.preventDefault();
    renderer.domElement.addEventListener('contextmenu', blockMenu);

    // ── Pivot marker ─────────────────────────────────────────────────────────
    // A ring lying flat in the galactic plane, marking what the camera turns
    // around. Without it, rotating and panning at this scale is disorienting —
    // there is no way to tell what you are circling.
    const pivotRing = new THREE.Mesh(
      new THREE.RingGeometry(0.86, 1, 64),
      new THREE.MeshBasicMaterial({
        color: 0x7fd4ff,
        transparent: true,
        opacity: 0.55,
        side: THREE.DoubleSide,
        depthTest: false,
      }),
    );
    pivotRing.renderOrder = 10;
    scene.add(pivotRing);

    const pivotSnapRing = new THREE.Mesh(
      new THREE.RingGeometry(0.93, 1, 64),
      new THREE.MeshBasicMaterial({
        color: 0xffd479,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthTest: false,
      }),
    );
    pivotSnapRing.renderOrder = 11;
    scene.add(pivotSnapRing);

    // ── Planned route ────────────────────────────────────────────────────────
    // Drawn in galactic coordinates, so the same line reads at every zoom: a
    // thread across the galaxy from far out, and the actual hop-by-hop path
    // once you are down among the stars. Depth testing is off so it stays
    // legible through the point cloud rather than being swallowed by it.
    const routeGroup = new THREE.Group();
    routeGroup.renderOrder = 9;
    scene.add(routeGroup);

    const clearRoute = () => {
      for (const child of [...routeGroup.children]) {
        routeGroup.remove(child);
        const mesh = child as Partial<THREE.Line>;
        mesh.geometry?.dispose();
        const material = mesh.material;
        if (Array.isArray(material)) material.forEach((m) => m.dispose());
        else material?.dispose();
      }
    };

    const drawRoute = (plan: RoutePlan | null) => {
      clearRoute();
      if (!plan) return;

      const at = (id: string): THREE.Vector3 => {
        const s = getSystemSummary(seed, id);
        return new THREE.Vector3(s.galacticPosition.x, s.galacticPosition.y, s.galacticPosition.z);
      };

      for (const leg of plan.legs) {
        if (leg.kind === 'warp') {
          const from = at(leg.from);
          const to = at(leg.to);

          // Warps are drawn distinctly because they cost a core — the eye
          // should be able to count them without reading the panel.
          routeGroup.add(
            new THREE.Line(
              new THREE.BufferGeometry().setFromPoints([from, to]),
              new THREE.LineDashedMaterial({
                color: 0xffd479,
                dashSize: 900,
                gapSize: 600,
                transparent: true,
                opacity: 0.95,
                depthTest: false,
              }),
            ).computeLineDistances(),
          );
          continue;
        }

        const points = leg.path.map(at);
        if (points.length < 2) continue;

        routeGroup.add(
          new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(points),
            new THREE.LineBasicMaterial({
              color: 0x7fd4ff,
              transparent: true,
              opacity: 0.9,
              depthTest: false,
            }),
          ),
        );
      }
    };

    // Fires straight away with the current route, so a target restored from a
    // previous session draws without waiting for something to change.
    const unsubscribeRoute = subscribeRoute(drawRoute);

    // ── Layers ───────────────────────────────────────────────────────────────
    const galaxy = buildGalaxyLayer(map);
    scene.add(galaxy.group);

    const tracker = new LodTracker(seed);
    let clusterLayer: Layer | null = null;
    let systemLayer: Layer | null = null;

    /**
     * Your ship, marked on the chart.
     *
     * Orange, matching the reticle and cursor in flight — on this map that
     * colour means "you". Drawn into the layer's own frame rather than the
     * scene, so it rides the system's transform and needs no per-frame work
     * beyond following its anchor.
     */
    const shipMarker = new THREE.Group();
    const shipAt = new THREE.Vector3();

    /**
     * What the ship is riding, when it is riding something.
     *
     * Null when the marker is a plain point on a chart — at galaxy and cluster
     * level, or adrift in open space. Otherwise the body it is parked at and
     * how far off it sits, both resolved fresh every frame.
     */
    let shipAnchor: {
      node: THREE.Object3D;
      offset: THREE.Vector3;
      frame: THREE.Object3D;
      /** Which system the offset is measured in, so a stale one is ignored. */
      systemId: string;
    } | null = null;

    /** True when the marker is a bare position rather than riding a body. */
    let shipLoose = false;
    const shipRing = new THREE.RingGeometry(0.66, 1, 4);
    const shipPaint = new THREE.MeshBasicMaterial({
      color: 0xff8a3d,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    shipMarker.add(new THREE.Mesh(shipRing, shipPaint));

    /**
     * Where the ship is, at whatever level is being looked at.
     *
     * Galaxy and cluster layers are both drawn in galactic coordinates, so at
     * those tiers the marker is simply parked at the system's own position and
     * parented to the scene. Only the system tier needs the local frame, and
     * only there is the ship a *place within* something rather than a dot on
     * a chart.
     */
    const placeShipAtRange = (position: { x: number; y: number; z: number }): void => {
      shipMarker.removeFromParent();
      shipAnchor = null;
      shipLoose = false;
      shipMarker.position.set(position.x, position.y, position.z);
      scene.add(shipMarker);
    };

    const placeShip = (layer: Layer, systemId: string): void => {
      /*
       * Read live, not from the prop.
       *
       * `ship` is captured when this effect is built and the effect is
       * deliberately never rebuilt, so by the time a player has zoomed to a
       * system it can be minutes stale — and it decides both *whether* a marker
       * is placed and where. The ref is the same object the cockpit writes to
       * every frame.
       */
      const current = shipRef?.current ?? ship;
      shipMarker.removeFromParent();
      shipAnchor = null;
      shipLoose = false;
      if (!layer.bodies) return;

      // Berthed. The station is already on the chart, so the marker goes on it
      // rather than at some offset from it — you are *at* the station, and a
      // ship drawn beside its own pad would be saying otherwise.
      if (!current && dockedAt && systemId === currentSystemId) {
        const station = layer.bodies.byName.get(dockedAt);
        if (!station) return;

        shipAnchor = {
          node: station,
          offset: new THREE.Vector3(),
          frame: layer.bodies.frame,
          systemId,
        };
        layer.bodies.frame.add(shipMarker);
        return;
      }

      if (!current || current.systemId !== systemId) return;

      const at = new THREE.Vector3(...current.offset).divideScalar(GAME_SCALE);

      /*
       * The saved position is an offset from whatever had hold of the ship, and
       * that body is still orbiting.
       *
       * This used to resolve the anchor *once*, when the layer was built, and
       * then leave the marker at that spot for as long as the map stayed open.
       * The station carried on round its planet and the marker did not, so a
       * ship parked at a berth was drawn further and further from it — which
       * was the whole complaint about the chart not matching the game. The
       * anchor is kept instead, and read every frame.
       */
      if (current.anchor) {
        const anchor = layer.bodies.byName.get(current.anchor);
        if (!anchor) return;

        shipAnchor = { node: anchor, offset: at, frame: layer.bodies.frame, systemId };
        layer.bodies.frame.add(shipMarker);
        return;
      }

      // Adrift in open space, with nothing to hang off. Still read live, since
      // the ship is under power even when nothing has hold of it.
      shipLoose = true;
      shipMarker.position.copy(at);
      layer.bodies.frame.add(shipMarker);
    };

    const labelNodes: {
      node: HTMLDivElement;
      position: THREE.Vector3;
      follow?: () => THREE.Vector3;
    }[] = [];
    const rebuildLabels = () => {
      labelLayer.replaceChildren();
      labelNodes.length = 0;

      for (const layer of [clusterLayer, systemLayer]) {
        for (const label of layer?.labels ?? []) {
          const node = document.createElement('div');
          node.textContent = label.text;
          node.style.cssText = `position:absolute;font-size:11px;white-space:nowrap;pointer-events:none;transform:translateX(-50%);text-shadow:0 1px 3px #05070d;color:${
            label.muted ? 'rgba(200,210,225,0.45)' : 'rgba(215,232,255,0.88)'
          }`;
          labelLayer.appendChild(node);
          labelNodes.push({ node, position: label.position, follow: label.follow });
        }
      }
    };

    const applyFocus = () => {
      const { tier, cluster, system } = tracker.focus;

      if (clusterLayer && cluster?.id !== clusterLayer.group.userData['clusterId']) {
        scene.remove(clusterLayer.group);
        clusterLayer.dispose();
        clusterLayer = null;
      }
      if (!clusterLayer && cluster) {
        clusterLayer = buildClusterLayer(seed, cluster);
        clusterLayer.group.userData['clusterId'] = cluster.id;
        scene.add(clusterLayer.group);
      }

      if (systemLayer && system?.id !== systemLayer.group.userData['systemId']) {
        scene.remove(systemLayer.group);
        systemLayer.dispose();
        systemLayer = null;
      }
      if (!systemLayer && system) {
        systemLayer = buildSystemLayer(seed, system);
        systemLayer.group.userData['systemId'] = system.id;
        scene.add(systemLayer.group);
        placeShip(systemLayer, system.id);
      }

      /*
       * At galaxy and cluster level the marker goes on the system we are in,
       * wherever the camera happens to be looking. It used to appear only once
       * the system layer had been built, which meant the chart could not answer
       * "where am I" until you had already found the answer.
       */
      if (tier !== 'system' && currentSystemId) {
        const home = navigator.get(currentSystemId as SystemId);
        if (home) placeShipAtRange(home.galacticPosition);
        else shipMarker.removeFromParent();
      }

      rebuildLabels();
      setFocusedSystemId(system?.id ?? null);

      /*
       * What the chart is looking at, as something that could be pinned.
       *
       * Most specific first, which is the rule the whole thing follows: a
       * system beats the cluster containing it, and a body beats the system
       * containing it — that last one is applied in `findSnapTarget`, because
       * only a snap knows which body was caught.
       *
       * The one refusal: the cluster you are already in. "Go to where you are"
       * is not a journey, and offering it as one would produce a route with no
       * legs and no way to tell that from a route that failed to plan.
       */
      const home = currentSystemId ? navigator.get(currentSystemId as SystemId) : null;

      if (system) {
        setUnderCursor({
          kind: 'system',
          label: system.name,
          detail: `system in ${cluster?.name ?? 'deep space'}`,
          systemId: system.id as SystemId,
          clusterId: system.clusterId as ClusterId,
        });
      } else if (cluster && cluster.id !== home?.clusterId) {
        const inside = tracker.systemsOf(cluster.id);

        setUnderCursor({
          kind: 'cluster',
          // Any system in it satisfies the journey, so it resolves to whichever
          // is nearest when the route is planned rather than to a fixed one.
          label: cluster.name,
          detail: `cluster · ${inside.length} systems`,
          systemId: (inside[0]?.id ?? '') as SystemId,
          clusterId: cluster.id as ClusterId,
        });
      } else {
        setUnderCursor(null);
      }

      if (tier === 'system' && system) {
        /*
         * Which cluster this system belongs to.
         *
         * The card named the system and then listed what was in it, which left
         * the one piece of context you need to place it on the chart -- where it
         * sits in the galaxy -- readable only by zooming back out.
         */
        const holding = map.find((entry) => entry.cluster.id === system.clusterId)?.cluster;

        setReadout({
          tier: 'system',
          title: system.name,
          lines: [
            ...(holding ? [`${holding.name} cluster`] : []),
            `${system.planetCount} planets`,
            `${system.stationCount} stations`,
            `${system.asteroidFieldCount} asteroid fields`,
            `${system.anomalyCount} anomalies`,
          ],
        });
      } else if (tier === 'cluster' && cluster) {
        const systems = map.find((e) => e.cluster.id === cluster.id)?.systems ?? [];
        setReadout({
          tier: 'cluster',
          title: cluster.name,
          lines: [
            `${systems.length} systems`,
            `${systems.reduce((n, s) => n + s.stationCount, 0)} stations`,
            `${systems.filter((s) => s.stationCount === 0).length} with no station`,
          ],
        });
      } else {
        setReadout({ tier: 'galaxy', title: 'Galaxy', lines: galaxyLines });
      }
    };
    applyFocus();

    // ── Fades ────────────────────────────────────────────────────────────────
    // Layers cross-fade by camera distance rather than snapping, so the tier
    // change is felt as detail resolving rather than as a cut.
    const updateFades = () => {
      const { cluster, system } = tracker.focus;

      // Only the focused cluster is suppressed in the point cloud, because the
      // cluster layer is already drawing those same systems as spheres and the
      // two would stack. Every other cluster stays visible — losing the whole
      // galaxy on approach leaves you working inside a void with no context.
      galaxy.setClusterHidden(cluster?.id ?? null);

      // Within the cluster, the placeholder ball for the system you have
      // actually entered has to go: the system layer draws its real star in the
      // same spot, and the two intersect.
      if (clusterLayer) {
        clusterLayer.group.traverse((object) => {
          const owner = object.userData['systemId'];
          if (typeof owner === 'string') object.visible = owner !== system?.id;
        });
      }
    };

    // ── Picking ──────────────────────────────────────────────────────────────
    const raycaster = new THREE.Raycaster();
    raycaster.params.Points = { threshold: 400 };
    const pointer = new THREE.Vector2();

    const toPointer = (event: MouseEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const activePickables = () => [
      ...(systemLayer?.pickables ?? []),
      ...(clusterLayer?.pickables ?? []),
      ...galaxy.pickables,
    ];

    // The hover readout is gone — it repeated whatever the focus panel already
    // said. Its raycast ran on every mouse move, so removing the panel removes
    // the work with it. `pickables` stays for when selection returns.
    void activePickables;
    void raycaster;
    void pointer;
    void toPointer;

    // ── Jumps ────────────────────────────────────────────────────────────────
    // Driven by a window event rather than a ref the component calls into.
    // The listener is registered by this effect, so it is wired to *this*
    // scene by construction — no chance of a stale instance receiving it.
    let pendingJump: Jump | null = openOn;
    const onJump = (event: Event) => {
      pendingJump = (event as CustomEvent<Jump>).detail;
    };
    window.addEventListener(JUMP_EVENT, onJump);

    /**
     * Point the camera at a system from a distance that lands on a given tier.
     *
     * The tier is a function of view distance, so arriving "at cluster level"
     * means backing off far enough that the system stops resolving but the
     * cluster still does — read off the same thresholds the tracker uses rather
     * than guessed, so the two cannot drift apart.
     */
    const frameSystem = (jump: Jump): void => {
      const summary = getSystemSummary(seed, jump.systemId);
      const target = new THREE.Vector3(
        summary.galacticPosition.x,
        summary.galacticPosition.y,
        summary.galacticPosition.z,
      );

      // Direction is fixed and slightly off-axis, so the galactic disc is seen
      // side-on rather than edge-on however you arrived.
      const heading = new THREE.Vector3(0.5, -0.72, 0.53).normalize();
      const distance = frameDistance(jump.level, map, summary.galacticPosition);

      controls.target.copy(target);
      camera.position.copy(target).addScaledVector(heading, distance);
      controls.update();
    };

    const consumeJump = () => {
      const jump = pendingJump;
      if (!jump) return;
      pendingJump = null;

      frameSystem(jump);

      // A jump is not a snap: whatever the pivot was easing toward before is
      // no longer where you asked to be, and nothing is being followed either.
      snapTarget = null;
      followed = null;
    };

    // ── Loop ─────────────────────────────────────────────────────────────────

    const scratch = new THREE.Vector3();
    const updateLabels = () => {
      for (const { node, position, follow } of labelNodes) {
        // Anything orbiting has moved since last frame, so its label has to be
        // re-read from the live object rather than from a baked position.
        scratch.copy(follow ? follow() : position).project(camera);
        if (scratch.z > 1 || Math.abs(scratch.x) > 1.15 || Math.abs(scratch.y) > 1.15) {
          node.style.display = 'none';
          continue;
        }
        node.style.display = '';
        node.style.left = `${(scratch.x * 0.5 + 0.5) * width}px`;
        node.style.top = `${(-scratch.y * 0.5 + 0.5) * height}px`;
      }
    };

    // ── Pivot behaviour ──────────────────────────────────────────────────────
    const { min: minHeight, max: maxHeight } = tracker.heightRange;
    let snapTarget: THREE.Vector3 | null = null;

    /**
     * A body the pivot has latched onto, if it is one that moves.
     *
     * Planets and moons are the only things on the chart that go anywhere, and
     * snapping to a *position* on one is snapping to where it used to be — a
     * few seconds later the pivot is beside empty space and the body has left.
     * Holding the node instead means the view stays on the thing rather than on
     * the place, which is what you asked for when you pointed at a planet.
     */
    let followed: THREE.Object3D | null = null;

    const viewDistance = () => camera.position.distanceTo(controls.target);

    /**
     * How near, on screen, something must be to the pivot to capture it.
     *
     * Tight for systems: inside a cluster they sit close together on screen, so
     * a generous radius grabs whichever happens to be nearest and feels like it
     * is choosing for you. Clusters are far apart and deserve the wider net.
     */
    const SNAP_PIXELS_SYSTEM = 38;
    const SNAP_PIXELS_CLUSTER = 110;

    const projectToScreen = (point: THREE.Vector3) => {
      const projected = point.clone().project(camera);
      return {
        x: (projected.x * 0.5 + 0.5) * width,
        y: (-projected.y * 0.5 + 0.5) * height,
        inFront: projected.z < 1,
      };
    };

    /**
     * Pick what the pivot should snap to, by how close it *looks* rather than
     * how close it is.
     *
     * Comparing in world space fails exactly when the map is most useful: seen
     * from an angle, a cluster sitting right under the marker can be tens of
     * thousands of units away in height and lose to something that merely
     * happens to be nearer in 3D. Screen distance is what the eye is judging,
     * so it is what the snap should use — and it makes the height difference
     * irrelevant for free.
     */
    const findSnapTarget = (): THREE.Vector3 | null => {
      const pivot = projectToScreen(controls.target);
      let best: THREE.Vector3 | null = null;
      let bestDistance = 0;

      let bestNode: THREE.Object3D | null = null;

      /**
       * What the winning candidate *is*, so it can be pinned.
       *
       * Only bodies reported themselves before, so steering onto a system or a
       * cluster caught it for the camera and told the navigation panel nothing
       * — the pin button stayed dim on everything except a body and whatever
       * the tier happened to have resolved. A snap is the act of choosing
       * something; all three kinds have to say what was chosen.
       */
      let bestTarget: NavTarget | null = null;

      const consider = (
        position: { x: number; y: number; z: number },
        node?: THREE.Object3D,
        describe?: () => NavTarget,
      ) => {
        const screen = projectToScreen(new THREE.Vector3(position.x, position.y, position.z));
        if (!screen.inFront) return;

        const distance = Math.hypot(screen.x - pivot.x, screen.y - pivot.y);
        if (distance >= bestDistance) return;
        bestDistance = distance;
        bestNode = node ?? null;
        bestTarget = describe?.() ?? null;
        best = new THREE.Vector3(
          position.x,
          position.y,
          // Bodies are inside a system and already within any sane height; the
          // clamp is for the galactic disc, and applying it to a planet would
          // drag the pivot off the thing it just caught.
          node ? position.z : THREE.MathUtils.clamp(position.z, minHeight, maxHeight),
        );
      };

      /*
       * Inside a system, the bodies themselves.
       *
       * Checked first and with the tightest net: once the system is drawn, its
       * planets and stations are what the chart is *for*, and snapping past
       * them to the star or to a neighbouring system would make the detail
       * level pointless. Only reached when a system layer exists, so at every
       * other tier this costs nothing.
       */
      const bodies = systemLayer?.bodies;
      if (bodies && tracker.focus.tier === 'system') {
        bestDistance = SNAP_PIXELS_SYSTEM;

        const system = tracker.focus.system;

        for (const [name, node] of bodies.byName) {
          consider(node.getWorldPosition(new THREE.Vector3()), node, () => ({
            kind: 'body',
            label: name,
            detail: system ? `in ${system.name}` : 'body',
            systemId: (system?.id ?? '') as SystemId,
            clusterId: (system?.clusterId ?? '') as ClusterId,
            bodyName: name,
          }));
        }
      }

      // Inside a cluster, systems are the thing worth landing on — that is what
      // makes it possible to wander from star to star without zooming out.
      const focused = tracker.focus.cluster;
      if (!best && focused) {
        bestDistance = SNAP_PIXELS_SYSTEM;

        for (const system of tracker.systemsOf(focused.id)) {
          consider(system.galacticPosition, undefined, () => ({
            kind: 'system',
            label: system.name,
            detail: `system in ${focused.name}`,
            systemId: system.id as SystemId,
            clusterId: system.clusterId as ClusterId,
          }));
        }
      }
      // Fall back to clusters, so panning away from a cluster can still catch
      // the next one without first zooming out.
      if (!best) {
        bestDistance = SNAP_PIXELS_CLUSTER;
        const homeCluster = currentSystemId
          ? navigator.get(currentSystemId as SystemId)?.clusterId
          : null;

        for (const cluster of tracker.clusters) {
          // The one you are already in is not a journey. See `underCursor`.
          const describe =
            cluster.id === homeCluster
              ? undefined
              : () => ({
                  kind: 'cluster' as const,
                  label: cluster.name,
                  detail: `cluster · ${tracker.systemsOf(cluster.id).length} systems`,
                  systemId: (tracker.systemsOf(cluster.id)[0]?.id ?? '') as SystemId,
                  clusterId: cluster.id as ClusterId,
                });

          consider(cluster.position, undefined, describe);
        }
      }

      followed = bestNode;

      // Whatever was caught is now what the pin button offers. Only when
      // something *was* caught: a drag into empty space should not silently
      // clear a destination you already picked.
      if (bestTarget) setUnderCursor(bestTarget);

      return best;
    };

    // OrbitControls fires 'end' for zooming as well as dragging, so the kind of
    // interaction has to be tracked separately: snapping on zoom would yank the
    // pivot sideways at the exact moment of crossing between detail levels,
    // which is when the view most needs to stay still.
    let lastInteractionWasDrag = false;
    /*
     * Escape closes the chart.
     *
     * On the window rather than on the canvas: the map has panels, inputs and
     * buttons, and after clicking any of them the canvas no longer has focus —
     * so a handler bound to it would work only until you touched anything.
     */
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      closeRef.current?.();
    };
    window.addEventListener('keydown', onEscape);

    /**
     * How far a pointer may move and still count as a press, in pixels.
     *
     * A finger never holds still, and a mouse barely does. Somewhere has to draw
     * the line between "tapped the map" and "dragged the map", and it has to be
     * generous enough for a thumb.
     */
    const DRAG_SLOP = 6;

    let pressing = false;
    let pressedAt = { x: 0, y: 0 };

    const onPointerDown = (event: PointerEvent) => {
      /*
       * Whether this was a drag is decided by *movement*, not by which button.
       *
       * It used to be `button === 1 || button === 2` — middle or right, the two
       * that pan and rotate a mouse. On touch `button` is always `0`, so a
       * one-finger rotate never counted as a drag and the map never snapped to
       * anything after one: the pivot simply stopped wherever the finger left
       * it. Measuring the movement instead is one rule for both, and it also
       * stops a middle-click that never moved from counting as a drag.
       */
      pressing = true;
      pressedAt = { x: event.clientX, y: event.clientY };
      lastInteractionWasDrag = false;

      /*
       * Touching the controls lets go of whatever was being followed.
       *
       * Without this the follow was a trap: the pivot was pulled back onto the
       * body every frame, so panning fought a spring that always won and the
       * only way off a planet was to leave the system entirely. Reaching for
       * the mouse is how you say you are done looking at it — and releasing
       * re-snaps, so catching the next thing still costs one drag.
       */
      followed = null;
      snapTarget = null;
    };
    /*
     * Decided while moving rather than on release.
     *
     * `OrbitControls` fires its own `end` on pointer-up, and whether that lands
     * before or after a listener of ours depends on registration order — which
     * is not something the snap should rest on. Setting the flag as the pointer
     * travels means it is already true by the time anything reads it.
     */
    const onPointerMove = (event: PointerEvent) => {
      if (!pressing || lastInteractionWasDrag) return;

      const moved = Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y);
      if (moved > DRAG_SLOP) lastInteractionWasDrag = true;
    };

    const onPointerRelease = () => {
      pressing = false;
    };

    const onWheel = () => {
      lastInteractionWasDrag = false;
    };
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerup', onPointerRelease);
    renderer.domElement.addEventListener('pointercancel', onPointerRelease);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: true });

    // Snapping happens on release, not while dragging — pulling the pivot
    // around under the cursor mid-drag fights the person doing the dragging.
    /**
     * Remember where the map is looking.
     *
     * Written on release rather than per frame — this is localStorage, and a
     * synchronous write inside the render loop is exactly the wrong place for
     * one. Position and pivot together are enough to restore the view: the tier
     * follows from the distance between them, so it needs no separate record.
     */
    const saveView = () => {
      try {
        const view: StoredView = {
          seed,
          target: controls.target.toArray(),
          position: camera.position.toArray(),
          // What the camera had resolved, so reopening can ask whether this view
          // was of anything still relevant. See where the map opens.
          systemId: tracker.focus.system?.id,
        };
        window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(view));
      } catch {
        // Private browsing and full quotas both land here. Losing the saved
        // view is a nuisance, not a failure worth interrupting the map for.
      }
    };

    const onInteractionEnd = () => {
      saveView();
      if (!lastInteractionWasDrag) return;
      snapTarget = findSnapTarget();
    };
    controls.addEventListener('end', onInteractionEnd);

    /*
     * Panning is free again — screen-space, following the camera's angle.
     *
     * It used to be pinned to the galactic plane. That was the right call while
     * the galaxy was effectively flat: a tilted screen-space pan drags the pivot
     * up out of the disc, and with nothing above the disc to look at, you ended
     * up in empty space with no obvious way back down.
     *
     * There is now a third dimension worth moving through — the galaxy is a
     * squashed ball a third as deep as it is wide — so height is somewhere you
     * want to go rather than somewhere you fall out of. And cluster snapping has
     * become the safety net that made the constraint unnecessary: releasing a
     * drag pulls the pivot onto a real cluster, which is by construction inside
     * the galaxy, so a pan that wanders off is one release from being recovered.
     */

    /**
     * Ease the pivot onto whatever the last release picked, carrying the camera
     * with it so the framing is preserved instead of the view lurching.
     *
     * Height comes along too — snapping is the only thing that moves the pivot
     * off the plane, since panning is confined to it.
     */
    const glideToSnap = () => {
      // Latched onto something that moves: the destination is wherever it is
      // *now*, re-read every frame. The same easing then does double duty — it
      // brings the view onto the body and then keeps it there, and because the
      // step is tiny once caught the tracking is invisible rather than a second
      // mechanism with its own feel.
      if (followed) snapTarget = followed.getWorldPosition(new THREE.Vector3());
      if (!snapTarget) return;

      const step = snapTarget.clone().sub(controls.target);
      // Doubled from 0.14: the previous glide read as sluggish.
      const SNAP_EASE = 0.28;
      if (step.lengthSq() < 1e-4 && !followed) {
        snapTarget = null;
        return;
      }

      step.multiplyScalar(SNAP_EASE);
      controls.target.add(step);
      camera.position.add(step);
    };

    const updatePivotMarker = () => {
      // Constant apparent size: the marker is a HUD element that happens to
      // live in the scene, so it must not shrink to nothing when zoomed out.
      const scale = viewDistance() * 0.035;

      pivotRing.position.copy(controls.target);
      pivotRing.scale.setScalar(scale);
      pivotSnapRing.position.copy(controls.target);
      pivotSnapRing.scale.setScalar(scale * 1.35);

      const material = pivotSnapRing.material as THREE.MeshBasicMaterial;
      material.opacity = THREE.MathUtils.lerp(material.opacity, snapTarget ? 0.7 : 0, 0.15);
    };

    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      consumeJump();
      controls.update();

      /*
       * The same clock the cockpit runs on.
       *
       * This used to accumulate its own delta from zero, which made the chart a
       * different world from the one you fly in twice over: bodies were four
       * hundred times faster, and their *phase* started wherever the map
       * happened to open. That was tolerable while the map was only a chart.
       * It stopped being tolerable the moment it started drawing your ship —
       * a marker beside a station that is not where the station actually is
       * says something false about where you are.
       *
       * `flightTime` is absolute and shared, so the chart and the canopy agree.
       * The cost is that the map no longer visibly turns, which was genuinely
       * nice to look at and is not worth being wrong for.
       */
      systemLayer?.animate?.(flightTime());

      // Held at a constant size on screen and square to the camera, so it stays
      // findable when zoomed out to a whole system — where the ship itself is
      // far below a pixel.
      /*
       * Where the ship is *now*.
       *
       * Two things move under this marker and both have to be read every frame.
       * The body it is parked at keeps orbiting — that is `shipAnchor` — and,
       * now that the chart is an overlay over a live cockpit, the ship itself
       * is flying. `shipRef` is the current flight state rather than the one
       * captured when the scene was built, so a marker watched during a cruise
       * actually crosses the system.
       */
      if (shipAnchor) {
        const live = shipRef?.current;
        if (live && live.systemId === shipAnchor.systemId) {
          shipAnchor.offset.set(...live.offset).divideScalar(GAME_SCALE);
        }

        shipAnchor.node.getWorldPosition(shipAt);
        shipAnchor.frame.worldToLocal(shipAt);
        shipMarker.position.copy(shipAt).add(shipAnchor.offset);
      } else if (shipRef?.current && shipLoose) {
        // Adrift, with nothing to hang off. The offset is the position.
        shipMarker.position.set(...shipRef.current.offset).divideScalar(GAME_SCALE);
      }

      if (shipMarker.parent) {
        shipMarker.quaternion.copy(camera.quaternion);
        const away = camera.position.distanceTo(shipMarker.getWorldPosition(shipAt));

        /*
         * Constant size on screen, which means undoing whatever the parent
         * scales it by.
         *
         * Inside a system the marker sits in a frame drawn at
         * `SYSTEM_RENDER_SCALE`, so it has to be divided back up. At galaxy and
         * cluster level it is parented to the scene at unit scale, and applying
         * the same correction made it twenty-eight times too big — a lozenge
         * across a third of the galaxy, which is roughly the fifty times it
         * looked.
         */
        const parentScale = shipMarker.parent === scene ? 1 : SYSTEM_RENDER_SCALE;
        shipMarker.scale.setScalar((away * 0.014) / parentScale);
      }

      glideToSnap();

      // Panning at galaxy scale and at planet scale need wildly different
      // speeds; tying them to the current view distance keeps the drag feeling
      // the same at every tier.
      controls.panSpeed = THREE.MathUtils.clamp(viewDistance() / 20000, 0.35, 1.6);

      // The pivot picks the subject; how close the camera is picks the detail.
      if (tracker.update(controls.target, viewDistance())) applyFocus();
      updateFades();
      updatePivotMarker();
      background.follow(camera.position);

      renderer.render(scene, camera);
      updateLabels();
    };
    tick();

    return () => {
      // Leaving for the system view is a teardown, not a release, so the last
      // interaction's save is not necessarily the current view — a jump or a
      // snap glide may have moved it since.
      saveView();
      cancelAnimationFrame(frame);
      renderer.domElement.removeEventListener('contextmenu', blockMenu);
      window.removeEventListener(JUMP_EVENT, onJump);
      unsubscribeRoute();
      clearRoute();
      controls.removeEventListener('end', onInteractionEnd);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerup', onPointerRelease);
      renderer.domElement.removeEventListener('pointercancel', onPointerRelease);
      renderer.domElement.removeEventListener('wheel', onWheel);
      background.dispose();
      controls.dispose();
      galaxy.dispose();
      clusterLayer?.dispose();
      systemLayer?.dispose();
      shipMarker.removeFromParent();
      window.removeEventListener('keydown', onEscape);
      shipRing.dispose();
      shipPaint.dispose();
      view.dispose();
      labelLayer.replaceChildren();
    };
  }, [seed, asBackdrop]);

  return (
    <>
      <div ref={mountRef} style={{ position: 'absolute', inset: 0 }} />
      <div ref={labelLayerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />

      {!asBackdrop && (
      <div style={{ ...PANEL, top: 16, left: 16, minWidth: 230 }}>
        <div style={{ fontSize: 11, opacity: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>
          {readout.tier}
        </div>
        <div style={{ fontSize: 16, marginBottom: 6 }}>{readout.title}</div>
        {readout.lines.map((line) => (
          <div key={line} style={{ opacity: 0.75 }}>{line}</div>
        ))}

        {/* Pinning what you are looking at, which is the half of navigation a
            search box cannot do: you steer onto a moon and it is simply there,
            with no name to type. */}
        {underCursor && underCursor.systemId && (
          <button
            type="button"
            // A destination only. Routes are planned from where the ship is.
            onClick={() => pinTarget(underCursor)}
            style={{
              marginTop: 8,
              width: '100%',
              background: 'rgba(190,120,255,0.16)',
              border: '1px solid rgba(190,120,255,0.42)',
              color: '#e8d8ff',
              font: 'inherit',
              fontSize: 12,
              padding: '4px 8px',
              cursor: 'pointer',
            }}
          >
            ◈ set target · {underCursor.label}
          </button>
        )}

        <div style={{ marginTop: 8, opacity: 0.5, fontSize: 11, lineHeight: 1.7 }}>
          <div>middle-drag &mdash; rotate</div>
          <div>right-drag &mdash; pan</div>
          <div>scroll &mdash; zoom</div>
        </div>
      </div>
      )}

      {!asBackdrop && (
        <SearchPanel
          navigator={navigator}
          focusedSystemId={focusedSystemId}
          underCursor={underCursor}
          hereSystemId={(currentSystemId ?? null) as SystemId | null}
          jumpRange={jumpRange}
          clusterRange={clusterRange}
          onGoTo={(systemId) => jumpTo(systemId, 'system')}
        />
      )}

    </>
  );
}

const buttonStyle: React.CSSProperties = {
  background: 'rgba(127,212,255,0.12)',
  border: '1px solid rgba(127,212,255,0.3)',
  color: '#dbeaff',
  font: 'inherit',
  fontSize: 12,
  padding: '3px 9px',
  cursor: 'pointer',
};

